import { ChangeDetectionStrategy, Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import {
  bl_applet_client_side_perm_to_role_link_RowClass,
  ClientSidePermissionRoleLinkContainerModel,
  ClientSidePermissionRoleLinksService,
  ClientSidePermissionTeamLinkService,
  ClientSideTenantPermissionService,
  Pagination,
  TenantRoleService,
  TeamService
} from 'blg-akaun-ts-lib';
import * as moment from 'moment';
import { PermissionFacade } from '../../../../../modules/permission/facades/permission.facade';
import { PermSetListingSearchModel } from '../../../../../models/advanced-search-models/perm-set-listing-search.model';
import { ClientSidePermissionModel } from '../../../../../models/client-side-perm.model';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../listing.utils';
import { PaginationComponent } from '../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { forkJoin, iif, Observable, of, zip, } from 'rxjs';
import { switchMap, filter, catchError, map } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { ClientSidePermissionsActions } from '../../../client-side-permissions-controller/actions';
import { ClientSidePermissionsSelectors } from '../../../client-side-permissions-controller/selectors';
import { PermissionStates } from '../../../permission-controller/root';
import { SearchQueryModel } from '../../../../../models/query.model';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-client-side-permission-listing',
  templateUrl: './client-side-permission-listing.component.html',
  styleUrls: ['./client-side-permission-listing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class ClientSidePermissionListingComponent extends ViewColumnComponent {

  protected readonly index = 0;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.localState.deactivateList));

  toggleColumn$: Observable<boolean>;

  // api visa
  apiVisa = AppConfig.apiVisa;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  searchModel = PermSetListingSearchModel;

  // initialise ag grid
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'single';

  // this applet guid
  appletGuid = sessionStorage.getItem('appletGuid');

  @ViewChild(PaginationComponent, { static: false }) private paginationComponent: PaginationComponent;

  //translation variable
  create_button: string;
  role_text: string;
  permission_code_text: string;
  permission_name_text: string;
  created_date_text: string;
  modified_date_text: string;

  constructor(
    private clientSidePermissionRoleLinksService: ClientSidePermissionRoleLinksService,
    private clientSidePermissionTeamLinkService: ClientSidePermissionTeamLinkService,
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private componentStore: ComponentStore<{ localState: LocalState }>,
    private clientSidePermissionService: ClientSideTenantPermissionService,
    private teamService: TeamService,
    private tenantRoleService: TenantRoleService,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.create_button = res.create_button_nocaps;
        this.role_text = res.role;
        this.permission_code_text = res.permission_code;
        this.permission_name_text = res.permission_name;
        this.created_date_text = res.created_date;
        this.modified_date_text = res.modified_date;
      });
    } else {
      this.create_button = 'Create';
      this.role_text = 'Role';
      this.permission_code_text = 'Permission Code';
      this.permission_name_text = 'Permission Name';
      this.created_date_text = 'Created Date';
      this.modified_date_text = 'Modified Date';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({ localState: a });
    });

    // define column definitions
    this.columnsDefs = [
      {
        headerName: this.role_text,
        field: 'RoleName',
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.permission_code_text,
        field: 'ClientSidePermCode',
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.permission_name_text,
        field: 'ClientSidePermName',
        cellStyle: { textAlign: 'left' }
      },
      {
        headerName: this.created_date_text,
        field: 'bl_applet_client_side_perm_to_role_link.created_date',
        headerClass: "text-center",
        // Change the date to a format that is easier to read.
        valueFormatter: ((params) => {
          let date = moment(params.value).format('YYYY-MM-DD HH:MM:ss');
          if (date === 'Invalid date') {
            date = null;
          }
          return date;
        })
      },
      {
        headerName: this.modified_date_text,
        field: 'app_perm_set_hdr.updated_date',
        headerClass: "text-center",
        // Change the date to a format that is easier to read.
        valueFormatter: ((params) => {
          let date = moment(params.value).format('YYYY-MM-DD HH:MM:ss');
          if (date === 'Invalid date') {
            date = null;
          }
          return date;
        })
      }
    ]
  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
  onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateAdd: true,
        deactivateList: false
      });
    // trigger the add permission set component to be shown
    this.viewColFacade.onNextAndReset(this.index, 1);
  }


  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    this.store.select(ClientSidePermissionsSelectors.selectUpdatePermSetListing)
      .subscribe(x => {
        if (x) {
          if (this.gridApi.getDisplayedRowCount() > 0) {
            this.gridApi.refreshServerSideStore();
          } else {
            this.gridApi.refreshServerSideStore(
              { purge: true }
            )
          }
          this.store.dispatch(ClientSidePermissionsActions.setUpdatePermSetListing({update: false}));
        }
      })
  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
  getDatasource(conditionalCriteria?) {
    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    let criteria = [
      { columnName: 'calcTotalRecords', operator: '=', value: 'true' },
      { columnName: 'applet_guid', operator: '=', value: this.appletGuid },
    ];
    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    const sortCriteria = [
      { columnName: 'orderBy', value: 'updated_date' },
      { columnName: 'order', value: 'DESC' }
    ];

    return {
      getRows: grid => {
        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const agFilter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length === 0 && sortModel.length === 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, criteria, sortCriteria);

        this.store.dispatch(ClientSidePermissionsActions.clientSidePermissionsInit({ request: grid.request }));
        this.subSink.sink = this.clientSidePermissionRoleLinksService.getByCriteria(pagination, this.apiVisa)
          .subscribe(a => {
            try {
              let newArrData: ClientSidePermissionModel[] = [];
              newArrData = a.data.map((data) => {
                let newObj: ClientSidePermissionModel = new ClientSidePermissionModel();
                newObj.bl_applet_client_side_perm_to_role_link = new bl_applet_client_side_perm_to_role_link_RowClass();
                newObj.bl_applet_client_side_perm_to_role_link = data.bl_applet_client_side_perm_to_role_link;

                return newObj;
              });

              const source: Observable<ClientSidePermissionModel>[] = [];
              newArrData.forEach(per => source.push(
                zip(
                  this.tenantRoleService.getByGuid(per.bl_applet_client_side_perm_to_role_link.role_guid.toString(), this.apiVisa).pipe(
                    catchError((err) => of(null))
                  ),
                  this.clientSidePermissionService.getByGuid(per.bl_applet_client_side_perm_to_role_link.client_perm_guid.toString(), this.apiVisa).pipe(
                    catchError((err) => of(null))
                  ),
                ).pipe(
                  map(([b_a, b_b]) => {
                    if (b_a && b_b) {
                      per.RoleName = b_a.data.app_mst_role.name;
                      per.ClientSidePermName = b_b.data.bl_applet_client_side_perm_dfn.perm_name;
                      per.ClientSidePermCode = b_b.data.bl_applet_client_side_perm_dfn.perm_code;
                    }
                    return per;
                  })
                )
              ));

              iif(() => newArrData.length > 0,
                forkJoin(source).pipe(map((b_inner) => {
                  newArrData = b_inner.filter(item => item !== null);
                  return newArrData;
                })),
                of(newArrData)
              ).subscribe(resolved => {
                console.log("DEBUG::::resolved", resolved);
                // put in data to the grid
                const data = sortOn(resolved).filter(entity => agFilter.by(entity));
                const totalRecords = agFilter.isFiltering ? a.totalRecords : data.length;

                // force pagination to go to the first page if filter is applied
                if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) {
                  this.paginationComponent.firstPage();
                }
                grid.success({
                  rowData: data,
                  rowCount: totalRecords
                });
              });
            } catch (error) {
              console.error("Error processing data:", error);
              grid.fail();
            }
          }, err => {
            console.error("Error fetching data:", err);
            grid.fail();
          });
      }
    };
  }


  /**
   * This function is called every time the user clicks on a row in the
   * AG grid.
   *
   * @param entity : the entity on the AG grid that was clicked on
   */
  onRowClicked(entity: ClientSidePermissionRoleLinkContainerModel) {
    this.store.dispatch(ClientSidePermissionsActions.selectClientSidePerm({clientSidePerm: entity}));
    // deactivate add and list in this local state
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance(this.index,
        {
          ...this.localState,
          deactivateAdd: false,
          deactivateList: true
        });
      // trigger the next view column
      this.viewColFacade.onNextAndReset(this.index, 2);
    }
  }

  /**
   * This function is called every time the user clicks on the toggle column
   * button. It wil trigger the screen to show either one or two columns at a
   * time.
   *
   * @param e : event
   */
  onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
  /**
   * Runs the search bar's query against the client-side-permission role-link
   * listing's own endpoint.
   *
   * Replaces the raw SQL that <app-advanced-search> built and posted to
   * SubQueryService (a SELECT over bl_applet_client_side_perm_to_role_link with
   * the applet scoping appended by this component). The search bar is now
   * <app-advanced-search-general>, which emits the keyword and the form value
   * instead of SQL.
   *
   * ClientSidePermissionRoleLinkUow.generateWhereClauseByCriteria joins
   * app_perm_set_hdr itself and honours `search_word` (ILIKE over ps.code,
   * ps.name and ps.description), plus `code` / `name` (ILIKE) and `description`
   * (exact) for the three advanced fields -- the same three fields
   * PermSetListingSearchModel declares. The applet scoping stays in this
   * component's default criteria, which getDatasource concatenates with what is
   * passed in.
   *
   * Note: before this change the model was already a SearchModelV2 while the
   * template still used <app-advanced-search>, which reads `query` and
   * `queryCallbacks` off the model -- neither of which a V2 model has. Searching
   * here threw a TypeError rather than filtering. That is fixed by the swap.
   */
  onSearch(e: SearchQueryModel) {
    const datasource = this.getDatasource(this.buildSearchCriteria(e));
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the emitted search, or undefined when it is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria(e: SearchQueryModel) {
    if (!e || e.isEmpty) {
      return undefined;
    }
    if (e.keyword) {
      return [{ columnName: 'search_word', operator: '=', value: e.keyword.trim() }];
    }
    const query: any = e.queryString || {};
    return [
      { columnName: 'code', operator: '=', value: query.code },
      { columnName: 'name', operator: '=', value: query.name },
      { columnName: 'description', operator: '=', value: query.description }
    ].filter(condition => condition.value);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
